import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert.jsx';
import Loader from '../components/Loader.jsx';
import { api } from '../api/client.js';

export default function EventSets() {
  const [params] = useSearchParams();
  const eventId = params.get('eventId');
  const tournamentSlug = params.get('tournamentSlug');
  const page = parseInt(params.get('page') || '1', 10);
  const perPage = parseInt(params.get('perPage') || '25', 10);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.eventSets(eventId, page, perPage)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, page, perPage]);

  function pageHref(p) {
    const sp = new URLSearchParams();
    sp.set('eventId', eventId);
    if (tournamentSlug) sp.set('tournamentSlug', tournamentSlug);
    sp.set('page', String(p));
    sp.set('perPage', String(perPage));
    return `/sets?${sp.toString()}`;
  }

  // The Search/TournamentEvents flow was removed; EventSets is reachable only
  // via direct URL now. Always send the user back to the dashboard.
  const backLink = { to: '/', label: '← Back to Dashboard' };

  if (!eventId) {
    return (
      <Alert kind="warning">
        Missing event ID. <Link to="/">Go to dashboard →</Link>
      </Alert>
    );
  }
  if (loading) return <Loader label="Loading…" />;
  if (error) {
    return (
      <>
        <Alert kind="danger">{error}</Alert>
        <Link to={backLink.to} className="btn btn-outline-secondary btn-sm">{backLink.label}</Link>
      </>
    );
  }
  if (!data) return null;

  const { sets, pagination, eventName } = data;

  return (
    <>
      <div className="mb-3">
        <Link to={backLink.to} className="btn btn-outline-secondary btn-sm">{backLink.label}</Link>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            {eventName ? (
              <>
                {eventName}
                <span className="text-muted fw-normal"> — Matches</span>
              </>
            ) : (
              <span>Matches (Sets)</span>
            )}
          </h4>
          <span className="text-muted">
            {pagination.total} total &middot; Page {pagination.page} of {pagination.totalPages}
          </span>
        </div>
        <div className="card-body p-0">
          {sets.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-neon table-hover mb-0">
                <thead>
                  <tr>
                    <th>Round</th>
                    <th>Player 1</th>
                    <th>Player 2</th>
                    <th>Score</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sets.map((set) => {
                    const p1 = set.slots[0];
                    const p2 = set.slots[1];
                    const isCompleted = set.completedAt != null || set.state === 3;
                    return (
                      <tr key={set.id}>
                        <td><strong>{set.fullRoundText || `Round ${set.round}`}</strong></td>
                        <td>
                          {p1?.entrant ? (
                            <span className={set.winnerId === p1.entrant.id ? 'winner' : ''}>
                              {p1.entrant.name}
                              {set.winnerId === p1.entrant.id && ' ★'}
                            </span>
                          ) : (
                            <span className="text-muted">TBD</span>
                          )}
                        </td>
                        <td>
                          {p2?.entrant ? (
                            <span className={set.winnerId === p2.entrant.id ? 'winner' : ''}>
                              {p2.entrant.name}
                              {set.winnerId === p2.entrant.id && ' ★'}
                            </span>
                          ) : (
                            <span className="text-muted">TBD</span>
                          )}
                        </td>
                        <td>
                          {set.displayScore && set.displayScore !== 'DQ' ? (
                            <code>{set.displayScore}</code>
                          ) : set.displayScore === 'DQ' ? (
                            <span className="text-warning">DQ</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {isCompleted ? (
                            <span className="badge badge-completed">Completed</span>
                          ) : (
                            <span className="badge badge-pending">Pending</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-center text-muted">No matches found for this event.</div>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            {pagination.page > 1 ? (
              <Link to={pageHref(pagination.page - 1)} className="btn btn-outline-secondary btn-sm">
                ← Previous
              </Link>
            ) : <span />}

            <span className="text-muted small">
              Page {pagination.page} of {pagination.totalPages}
            </span>

            {pagination.page < pagination.totalPages ? (
              <Link to={pageHref(pagination.page + 1)} className="btn btn-outline-secondary btn-sm">
                Next →
              </Link>
            ) : <span />}
          </div>
        )}
      </div>
    </>
  );
}
